import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getProfitSettings, saveProfitSettings, getProfitSettingsChangeLog } from "../services/api";
import { Button, Badge, LoadingBar, Tabs, Segmented } from "./common/ui";
import InsightCacheCard from "./InsightCacheCard";

// Mirrors backend/profitCalc.js's SIMPLE_ONLY / CH_COMPLEX / ALL_CH / CH_LABELS —
// duplicated here (not imported) because that module is CommonJS for the Node
// backend and this is browser code. If the channel list or labels ever change on
// the backend, update this block the same way. See profitCalc.js's top comment
// for the full settings shape this editor reproduces field-for-field.
const SIMPLE_ONLY = ["amazon", "nykaa", "ajio", "tatacliq", "soch"];
const CH_COMPLEX = ["flipkart", "myntra"];
const ALL_CH = ["myntra", "amazon", "nykaa", "ajio", "tatacliq", "soch", "flipkart"];
export const CH_LABELS_FALLBACK = { ajio: "Ajio", nykaa: "Nykaa", amazon: "Amazon", tatacliq: "TataCliq", soch: "Soch", flipkart: "Flipkart", myntra: "Myntra" };
const LEVELS = ["1", "2", "3", "4"];
const ZONES = [{ key: "local", label: "Local" }, { key: "zonal", label: "Zonal" }, { key: "national", label: "National" }];

const NON_RMG_KEYS = ["saree", "unstitch", "unstitch ghagra choli"];
function normCat(raw) {
  let s = (raw || "").toLowerCase().trim().replace(/\s+/g, " ");
  if (s.endsWith("sses") && s.length > 4) return s.slice(0, -2);
  if (s.endsWith("ss")) return s;
  if (s.endsWith("s") && s.length > 2) return s.slice(0, -1);
  return s;
}
const isNonRMG = (k) => NON_RMG_KEYS.some((n) => k === n || k.includes(n));
const displayCat = (k) => (k ? k.charAt(0).toUpperCase() + k.slice(1) : k);

function initSettings() {
  const s = {};
  for (const c of SIMPLE_ONLY) s[c] = { commissionType: "pct", commissionPct: "", commissionFlat: "", commissionBasis: "GOT", freightType: "flat", freightPct: "", freightFlat: "", freightOnReturn: "yes", returnSlabs: {} };
  s.nykaa.freightOnReturn = "no";
  s.flipkart = { categoryLevels: {}, categories: {} };
  s.myntra = { categoryLevels: {}, categories: {} };
  s.flipkartGT = {}; s.myntraGT = {}; s.flipkartFixedFee = []; s.myntraFixedFee = [];
  const rld = () => ({ returnSlabs: {}, split: {}, charges: { "1": { local: "", zonal: "", national: "" }, "2": { local: "", zonal: "", national: "" }, "3": { local: "", zonal: "", national: "" }, "4": { local: "", zonal: "", national: "" } } });
  s.flipkartRL = rld(); s.myntraRL = rld(); s._products = [];
  return s;
}

// ── Shared small inputs, restyled with sochonereorder's tokens (no Gold branding) ──
const inputStyle = {
  padding: "6px 9px", borderRadius: 6, border: "1px solid var(--border)",
  background: "var(--bg-input)", color: "var(--text)", fontSize: 13, boxSizing: "border-box", width: "100%",
};
const slabGrid = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 32px", gap: 8, alignItems: "center", marginBottom: 6 };
const slabHeadGrid = { ...slabGrid, fontSize: 11, color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" };
const delBtn = { width: 26, height: 26, borderRadius: 6, border: "1px solid var(--danger)", background: "transparent", color: "var(--danger)", cursor: "pointer", fontSize: 14, lineHeight: 1 };
const addBtn = { padding: "5px 12px", borderRadius: 6, border: "1px solid var(--accent)", background: "transparent", color: "var(--accent)", cursor: "pointer", fontSize: 12, fontWeight: 700 };
const cardBox = { border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 14, background: "var(--bg-card)", marginBottom: 12 };
const sectionLabel = { fontSize: 11.5, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 10 };
const nonRmgBadge = <Badge tone="warning" style={{ fontSize: 10, padding: "1px 6px" }}>Non-RMG</Badge>;

function Toggle({ options, value, onChange, disabled }) {
  return (
    <div role="group" style={{ display: "inline-flex", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", overflow: "hidden" }}>
      {options.map((opt) => (
        <button
          key={opt.value}
          disabled={disabled}
          aria-pressed={value === opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            padding: "5px 12px", fontSize: 12, fontWeight: 650, border: "none", transition: "background var(--t-fast), color var(--t-fast)",
            cursor: disabled ? "default" : "pointer",
            background: value === opt.value ? "var(--accent)" : "transparent",
            color: value === opt.value ? "#fff" : "var(--text-muted)",
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function NumInput({ value, onChange, placeholder, suffix, disabled }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <input type="number" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} />
      {suffix && <span style={{ fontSize: 12, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{suffix}</span>}
    </div>
  );
}

// Generic min/max/fee slab list editor — reused for return-% slabs, GT slabs,
// fixed-fee slabs, and commission-by-price-range slabs (same 3-column shape).
function SlabList({ slabs, onAdd, onUpdate, onDelete, feeLabel = "Fee", feePlaceholder = "0", disabled }) {
  return (
    <div>
      <div style={slabHeadGrid}><span>Min SP</span><span>Max SP</span><span>{feeLabel}</span><span /></div>
      {(slabs || []).length === 0 && <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", padding: "4px 0 8px" }}>No slabs yet.</div>}
      {(slabs || []).map((s, i) => (
        <div key={i} style={slabGrid}>
          <input type="number" disabled={disabled} value={s.min} onChange={(e) => onUpdate(i, "min", e.target.value)} placeholder="0" style={inputStyle} />
          <input type="number" disabled={disabled} value={s.max} onChange={(e) => onUpdate(i, "max", e.target.value)} placeholder="∞" style={inputStyle} />
          <input type="number" disabled={disabled} value={s.fee ?? s.commission} onChange={(e) => onUpdate(i, s.commission !== undefined ? "commission" : "fee", e.target.value)} placeholder={feePlaceholder} style={inputStyle} />
          {!disabled && <button onClick={() => onDelete(i)} style={delBtn} title="Remove">×</button>}
        </div>
      ))}
      {!disabled && <button onClick={onAdd} style={{ ...addBtn, marginTop: 4 }}>+ Add Slab</button>}
    </div>
  );
}

// Per-category accordion around a SlabList — used for both return-% slabs
// (keyed by settings[ch].returnSlabs / settings[`${ch}RL`].returnSlabs).
function PerCategorySlabs({ categories, returnSlabs, onChange, disabled }) {
  const [open, setOpen] = useState({});
  if (!categories.length) return <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>Add product categories first (bottom of this tab).</div>;
  const addSlab = (ck) => { const cur = { ...(returnSlabs || {}) }; cur[ck] = [...(cur[ck] || []), { min: "", max: "", fee: "" }]; onChange(cur); };
  const updSlab = (ck, i, f, v) => { const cur = { ...(returnSlabs || {}) }; const arr = [...(cur[ck] || [])]; arr[i] = { ...arr[i], [f]: v }; cur[ck] = arr; onChange(cur); };
  const delSlab = (ck, i) => { const cur = { ...(returnSlabs || {}) }; cur[ck] = (cur[ck] || []).filter((_, j) => j !== i); onChange(cur); };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {categories.map((ck) => {
        const slabs = (returnSlabs || {})[ck] || [];
        const isOpen = !!open[ck];
        return (
          <div key={ck} style={{ border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--card)" }}>
            <button
              aria-expanded={isOpen}
              onClick={() => setOpen((p) => ({ ...p, [ck]: !p[ck] }))}
              style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "transparent", border: "none", cursor: "pointer" }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", display: "flex", alignItems: "center", gap: 8 }}>
                {displayCat(ck)}
                {isNonRMG(ck) && nonRmgBadge}
                {slabs.length > 0 && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>({slabs.length} slab{slabs.length !== 1 ? "s" : ""})</span>}
              </span>
              <span style={{ color: "var(--text-muted)", fontSize: 11 }}>{isOpen ? "▲" : "▼"}</span>
            </button>
            {isOpen && (
              <div style={{ padding: "0 12px 12px" }}>
                <SlabList
                  slabs={slabs}
                  disabled={disabled}
                  onAdd={() => addSlab(ck)}
                  onUpdate={(i, f, v) => updSlab(ck, i, f, v)}
                  onDelete={(i) => delSlab(ck, i)}
                  feeLabel="Return %"
                  feePlaceholder="%"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── SIMPLE_ONLY channel panel (amazon, nykaa, ajio, tatacliq, soch) ────────────
function SimpleChannelPanel({ ch, settings, onUpdate, categories, disabled }) {
  const s = settings[ch] || {};
  const ct = s.commissionType || "pct", ft = s.freightType || "flat", basis = s.commissionBasis || "GOT";
  return (
    <div>
      <div style={cardBox}>
        <div style={sectionLabel}>Commission</div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <Toggle disabled={disabled} options={[{ value: "GOT", label: "GOT" }, { value: "NOT", label: "NOT" }]} value={basis} onChange={(v) => onUpdate(ch, "commissionBasis", v)} />
          <Toggle disabled={disabled} options={[{ value: "pct", label: "%" }, { value: "flat", label: "₹ Flat" }]} value={ct} onChange={(v) => onUpdate(ch, "commissionType", v)} />
        </div>
        {ct === "pct"
          ? <NumInput disabled={disabled} value={s.commissionPct} onChange={(v) => onUpdate(ch, "commissionPct", v)} placeholder="18" suffix="%" />
          : <NumInput disabled={disabled} value={s.commissionFlat} onChange={(v) => onUpdate(ch, "commissionFlat", v)} placeholder="150" suffix="₹" />}
      </div>
      <div style={cardBox}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={sectionLabel}>Freight</div>
          <Toggle disabled={disabled} options={[{ value: "flat", label: "₹ Flat" }, { value: "pct", label: "%" }]} value={ft} onChange={(v) => onUpdate(ch, "freightType", v)} />
        </div>
        {ft === "flat"
          ? <NumInput disabled={disabled} value={s.freightFlat} onChange={(v) => onUpdate(ch, "freightFlat", v)} placeholder="50" suffix="₹" />
          : <NumInput disabled={disabled} value={s.freightPct} onChange={(v) => onUpdate(ch, "freightPct", v)} placeholder="3" suffix="%" />}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Charged again on returns?</span>
          <Toggle disabled={disabled} options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} value={s.freightOnReturn || "yes"} onChange={(v) => onUpdate(ch, "freightOnReturn", v)} />
        </div>
      </div>
      <div style={cardBox}>
        <div style={{ ...sectionLabel, marginBottom: 10 }} title="Effective return % = max(style-wide %, band %).">Return % by Product × Price Band ⓘ</div>
        <PerCategorySlabs categories={categories} returnSlabs={s.returnSlabs || {}} onChange={(nv) => onUpdate(ch, "returnSlabs", nv)} disabled={disabled} />
      </div>
    </div>
  );
}

// ── CH_COMPLEX channel panels (myntra, flipkart) ───────────────────────────────
function CategoryLevelsPanel({ ch, categories, settings, onUpdate, disabled }) {
  const s = settings[ch] || {};
  const catLevels = s.categoryLevels || {};
  if (!categories.length) return <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>Add product categories first.</div>;
  return (
    <div style={cardBox}>
      <div style={sectionLabel}>Category → Level</div>
      {categories.map((ck) => {
        const level = catLevels[ck] || "1";
        return (
          <div key={ck} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
            <span style={{ fontSize: 13, color: "var(--text)", display: "flex", alignItems: "center", gap: 8 }}>
              {displayCat(ck)}
              {isNonRMG(ck) && nonRmgBadge}
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              {LEVELS.map((l) => (
                <button
                  key={l}
                  disabled={disabled}
                  aria-pressed={level === l}
                  aria-label={`Level ${l}`}
                  onClick={() => onUpdate(ch, "categoryLevels", { ...catLevels, [ck]: l })}
                  style={{
                    width: 30, height: 28, borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: disabled ? "default" : "pointer",
                    border: level === l ? "1px solid var(--accent)" : "1px solid var(--border)",
                    background: level === l ? "var(--accent)" : "var(--bg-input)",
                    color: level === l ? "#fff" : "var(--text-muted)",
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GTChargesPanel({ ch, settings, onGTUpdate, disabled }) {
  const key = `${ch}GT`;
  const allGT = settings[key] || {};
  const [open, setOpen] = useState({ 1: true });
  const addSlab = (lv) => onGTUpdate(key, lv, [...(allGT[lv] || []), { min: "", max: "", fee: "" }]);
  const updSlab = (lv, i, f, v) => { const cur = [...(allGT[lv] || [])]; cur[i] = { ...cur[i], [f]: v }; onGTUpdate(key, lv, cur); };
  const delSlab = (lv, i) => onGTUpdate(key, lv, (allGT[lv] || []).filter((_, j) => j !== i));
  return (
    <div>
      <div style={sectionLabel}>GT Charge per Level × SP Range</div>
      {LEVELS.map((lv) => {
        const slabs = allGT[lv] || [];
        const isOpen = !!open[lv];
        return (
          <div key={lv} style={{ ...cardBox, marginBottom: 10 }}>
            <button aria-expanded={isOpen} onClick={() => setOpen((p) => ({ ...p, [lv]: !p[lv] }))} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "transparent", border: "none", cursor: "pointer", padding: 0, marginBottom: isOpen ? 10 : 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Level {lv} {slabs.length > 0 && <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: 11 }}>({slabs.length})</span>}</span>
              <span style={{ color: "var(--text-muted)", fontSize: 11 }}>{isOpen ? "▲" : "▼"}</span>
            </button>
            {isOpen && (
              <SlabList slabs={slabs} disabled={disabled} onAdd={() => addSlab(lv)} onUpdate={(i, f, v) => updSlab(lv, i, f, v)} onDelete={(i) => delSlab(lv, i)} feeLabel="GT (₹)" feePlaceholder="₹" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function FixedFeePanel({ ch, settings, onSlabUpdate, disabled }) {
  const key = `${ch}FixedFee`;
  const slabs = settings[key] || [];
  const add = () => onSlabUpdate(key, [...slabs, { min: "", max: "", fee: "" }]);
  const upd = (i, f, v) => { const n = [...slabs]; n[i] = { ...n[i], [f]: v }; onSlabUpdate(key, n); };
  const del = (i) => onSlabUpdate(key, slabs.filter((_, j) => j !== i));
  return (
    <div style={cardBox}>
      <div style={sectionLabel}>Fixed Fee — slab-wise on SP after GT</div>
      <SlabList slabs={slabs} disabled={disabled} onAdd={add} onUpdate={upd} onDelete={del} feeLabel="Fee (₹)" feePlaceholder="₹" />
    </div>
  );
}

function CommissionPanel({ ch, settings, onUpdate, categories, disabled }) {
  const s = settings[ch] || {};
  const cs = s.categories || {};
  const catLevels = s.categoryLevels || {};
  const [open, setOpen] = useState({});
  const updCat = (ck, f, v) => onUpdate(ch, "categories", { ...cs, [ck]: { ...(cs[ck] || { priceRanges: [] }), [f]: v } });
  const addR = (ck) => updCat(ck, "priceRanges", [...(cs[ck]?.priceRanges || []), { min: "", max: "", commission: "" }]);
  const updR = (ck, i, f, v) => { const r = [...(cs[ck]?.priceRanges || [])]; r[i] = { ...r[i], [f]: v }; updCat(ck, "priceRanges", r); };
  const delR = (ck, i) => updCat(ck, "priceRanges", (cs[ck]?.priceRanges || []).filter((_, j) => j !== i));
  if (!categories.length) return <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>Add product categories first.</div>;
  return (
    <div>
      <div style={sectionLabel}>Commission % on (SP − GT) by Category × Price Range</div>
      {categories.map((ck) => {
        const conf = cs[ck] || {};
        const isOpen = !!open[ck];
        const ranges = conf.priceRanges || [];
        const lv = catLevels[ck] || "1";
        return (
          <div key={ck} style={{ ...cardBox, marginBottom: 10 }}>
            <button aria-expanded={isOpen} onClick={() => setOpen((p) => ({ ...p, [ck]: !p[ck] }))} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "transparent", border: "none", cursor: "pointer", padding: 0, marginBottom: isOpen ? 10 : 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: 8 }}>
                {displayCat(ck)}
                <Badge style={{ fontSize: 10, padding: "1px 6px" }}>Lvl {lv}</Badge>
                {ranges.length > 0 && <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: 11 }}>({ranges.length} range{ranges.length !== 1 ? "s" : ""})</span>}
              </span>
              <span style={{ color: "var(--text-muted)", fontSize: 11 }}>{isOpen ? "▲" : "▼"}</span>
            </button>
            {isOpen && (
              <SlabList slabs={ranges} disabled={disabled} onAdd={() => addR(ck)} onUpdate={(i, f, v) => updR(ck, i, f, v)} onDelete={(i) => delR(ck, i)} feeLabel="Comm %" feePlaceholder="%" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ZoneSplitPanel({ categories, splits, onChange, disabled }) {
  const [open, setOpen] = useState({});
  const updSplit = (ck, zone, val) => { const cur = { ...(splits || {}) }; cur[ck] = { ...(cur[ck] || {}), [zone]: val }; onChange(cur); };
  if (!categories.length) return <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>Add product categories first.</div>;
  return (
    <div>
      {categories.map((ck) => {
        const sp = (splits || {})[ck] || {};
        const total = (+(sp.local || 0)) + (+(sp.zonal || 0)) + (+(sp.national || 0));
        const ok = Math.abs(total - 100) < 0.01;
        const isOpen = !!open[ck];
        return (
          <div key={ck} style={{ ...cardBox, marginBottom: 10 }}>
            <button aria-expanded={isOpen} onClick={() => setOpen((p) => ({ ...p, [ck]: !p[ck] }))} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "transparent", border: "none", cursor: "pointer", padding: 0, marginBottom: isOpen ? 10 : 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{displayCat(ck)}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: total === 0 ? "var(--text-muted)" : ok ? "var(--success)" : "var(--danger)" }}>{total > 0 ? `${total.toFixed(0)}%` : "—"}</span>
            </button>
            {isOpen && (
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                  {ZONES.map((z) => (
                    <NumInput key={z.key} disabled={disabled} value={sp[z.key] || ""} onChange={(v) => updSplit(ck, z.key, v)} placeholder="0" suffix="%" />
                  ))}
                </div>
                {!ok && total > 0 && <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 6 }}>⚠ Must total 100%</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RLChargesTable({ ch, settings, onUpdate, disabled }) {
  const rlKey = `${ch}RL`;
  const charges = (settings[rlKey] || {}).charges || {};
  const updCharge = (level, zone, val) => onUpdate(rlKey, "charges", { ...charges, [level]: { ...(charges[level] || {}), [zone]: val } });
  return (
    <div style={cardBox}>
      <div style={sectionLabel}>Reverse-Logistics Charge (₹) — Level × Zone</div>
      <div className="table-wrap" style={{ background: "var(--card)" }}>
        <table>
          <thead>
            <tr>
              <th style={th}>Level</th>
              {ZONES.map((z) => <th key={z.key} style={{ ...th, textAlign: "right" }}>{z.label} (₹)</th>)}
            </tr>
          </thead>
          <tbody>
            {LEVELS.map((lv) => {
              const lc = charges[lv] || {};
              return (
                <tr key={lv}>
                  <td style={{ ...td, fontWeight: 700 }}>{lv}</td>
                  {ZONES.map((z) => (
                    <td key={z.key} style={{ ...td, textAlign: "right" }}>
                      <input type="number" disabled={disabled} value={lc[z.key] || ""} onChange={(e) => updCharge(lv, z.key, e.target.value)} placeholder="₹" style={{ ...inputStyle, width: 90, textAlign: "right", marginLeft: "auto" }} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ComplexChannelPanel({ ch, settings, onUpdate, onGTUpdate, categories, disabled }) {
  const [sub, setSub] = useState("levels");
  const SUBS = [
    { key: "levels", label: "Category Levels" },
    { key: "gt", label: "GT Charges" },
    { key: "ff", label: "Fixed Fee" },
    { key: "commission", label: "Commission" },
    { key: "rl", label: "Reverse Logistics" },
  ];
  return (
    <div>
      <div style={{ marginBottom: 14, maxWidth: "100%", overflowX: "auto" }}>
        <Segmented ariaLabel="Channel settings section" value={sub} onChange={setSub} options={SUBS.map((s) => ({ value: s.key, label: s.label }))} />
      </div>
      {sub === "levels" && <CategoryLevelsPanel ch={ch} categories={categories} settings={settings} onUpdate={onUpdate} disabled={disabled} />}
      {sub === "gt" && <GTChargesPanel ch={ch} settings={settings} onGTUpdate={onGTUpdate} disabled={disabled} />}
      {sub === "ff" && <FixedFeePanel ch={ch} settings={settings} onSlabUpdate={onGTUpdate} disabled={disabled} />}
      {sub === "commission" && <CommissionPanel ch={ch} settings={settings} onUpdate={onUpdate} categories={categories} disabled={disabled} />}
      {sub === "rl" && (
        <div>
          <div style={cardBox}>
            <div style={sectionLabel}>Product Return % by Price Band</div>
            <PerCategorySlabs categories={categories} returnSlabs={(settings[`${ch}RL`] || {}).returnSlabs || {}} onChange={(nv) => onUpdate(`${ch}RL`, "returnSlabs", nv)} disabled={disabled} />
          </div>
          <div style={cardBox}>
            <div style={sectionLabel}>Return Distribution by Zone × Category (must total 100%)</div>
            <ZoneSplitPanel categories={categories} splits={(settings[`${ch}RL`] || {}).split || {}} onChange={(nv) => onUpdate(`${ch}RL`, "split", nv)} disabled={disabled} />
          </div>
          <RLChargesTable ch={ch} settings={settings} onUpdate={onUpdate} disabled={disabled} />
        </div>
      )}
    </div>
  );
}

// ── Product categories manager (settings._products) ────────────────────────────
function ProductCategoriesPanel({ products, onChange, disabled }) {
  const [newP, setNewP] = useState("");
  const add = () => {
    const k = normCat(newP.trim());
    if (!k || products.includes(k)) { setNewP(""); return; }
    onChange([...products, k].sort());
    setNewP("");
  };
  const remove = (k) => onChange(products.filter((p) => p !== k));
  return (
    <div style={cardBox}>
      <div style={{ ...sectionLabel, marginBottom: 10 }} title="Pre-defined categories drive every category-keyed table above (return slabs, levels, commission ranges).">Product Categories ⓘ</div>
      {!disabled && (
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <input
            value={newP}
            onChange={(e) => setNewP(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="e.g. Kurti, Dress, Saree"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button onClick={add} style={addBtn}>+ Add</button>
        </div>
      )}
      {products.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>No categories defined yet.</div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {products.map((ck) => (
            <div key={ck} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 6px 5px 12px", borderRadius: 20, border: "1px solid var(--border)", background: "var(--bg-input)" }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{displayCat(ck)}</span>
              {isNonRMG(ck) && nonRmgBadge}
              {!disabled && (
                <button onClick={() => remove(ck)} title="Remove" style={{ width: 18, height: 18, borderRadius: "50%", border: "none", background: "transparent", color: "var(--danger)", cursor: "pointer", fontSize: 13, lineHeight: 1 }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Change log (audit trail) — compact raw view, not a primary UX surface ─────
function ChangeLogModal({ onClose, showToast }) {
  const [log, setLog] = useState(null);
  useEffect(() => {
    getProfitSettingsChangeLog()
      .then((d) => setLog(d.log || []))
      .catch((e) => { setLog([]); if (showToast) showToast(e.response?.data?.detail || "Failed to load change log", "error"); });
  }, [showToast]);

  return (
    <div className="img-modal-overlay" onClick={onClose}>
      <div className="img-modal-box" onClick={(e) => e.stopPropagation()} style={{ minWidth: 480, maxWidth: 720, maxHeight: "80vh", overflowY: "auto" }}>
        <button className="img-modal-close" onClick={onClose}>✕</button>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 12 }}>Profit Settings — Change Log</div>
        {log === null ? (
          <div style={{ padding: "16px 0" }}><LoadingBar /></div>
        ) : log.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--text-muted)", padding: "20px 0", textAlign: "center" }}>No changes recorded yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
            {log.map((entry, i) => (
              <div key={i} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>
                  {entry.changed_by || "Admin"} — {entry.changed_at ? new Date(entry.changed_at).toLocaleString() : "—"}
                </div>
                <details>
                  <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--accent)" }}>View raw diff (JSON)</summary>
                  <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                    <pre style={{ flex: 1, minWidth: 200, fontSize: 11, background: "var(--bg-card)", border: "1px solid var(--border)", padding: 8, borderRadius: 6, overflowX: "auto", maxHeight: 200, color: "var(--danger)" }}>{JSON.stringify(entry.old_value, null, 2)}</pre>
                    <pre style={{ flex: 1, minWidth: 200, fontSize: 11, background: "var(--bg-card)", border: "1px solid var(--border)", padding: 8, borderRadius: 6, overflowX: "auto", maxHeight: 200, color: "var(--success)" }}>{JSON.stringify(entry.new_value, null, 2)}</pre>
                  </div>
                </details>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const CHANNEL_TABS = ALL_CH.map((c) => ({ key: c, label: CH_LABELS_FALLBACK[c] }));

export default function ProfitSettingsPanel({ showToast, isAdmin }) {
  const [settings, setSettings] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [activeCh, setActiveCh] = useState("myntra");
  const [saving, setSaving] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const disabled = !isAdmin;
  const flipkartMirror = settings ? settings.flipkart_mirror_myntra !== false : true; // default ON when absent

  const load = useCallback(() => {
    setSettings(null);
    getProfitSettings()
      .then((d) => { setSettings(d.settings || initSettings()); setUpdatedAt(d.updated_at); })
      .catch((e) => {
        setSettings(initSettings());
        if (showToast) showToast(e.response?.data?.detail || "Failed to load profit settings", "error");
      });
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => (settings?._products || []), [settings]);

  const onUpdate = useCallback((ch, field, value) => {
    setSettings((prev) => ({ ...prev, [ch]: { ...(prev[ch] || {}), [field]: value } }));
  }, []);
  // GT / Fixed Fee slabs live at settings[key] directly (key = `${ch}GT` or `${ch}FixedFee`),
  // not nested under settings[ch] — separate updater to match that shape.
  const onRootUpdate = useCallback((key, level, value) => {
    setSettings((prev) => {
      if (level === undefined) return { ...prev, [key]: value };
      // FixedFee arrays call onRootUpdate(key, arrayValue) with 2 args — detect that case.
      if (Array.isArray(level)) return { ...prev, [key]: level };
      return { ...prev, [key]: { ...(prev[key] || {}), [level]: value } };
    });
  }, []);
  const onProductsChange = useCallback((list) => setSettings((prev) => ({ ...prev, _products: list })), []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveProfitSettings(settings);
      if (showToast) showToast("✅ Profit settings saved", "info");
      load();
    } catch (e) {
      if (showToast) showToast(e.response?.data?.detail || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div style={{ padding: "24px 0" }}><LoadingBar /><div style={{ marginTop: 10, fontSize: 13, color: "var(--text-muted)" }}>Loading profit settings…</div></div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          Last updated: {updatedAt ? new Date(updatedAt).toLocaleString() : "never"}
          <span aria-hidden="true">·</span>
          <Button size="sm" variant="ghost" style={{ color: "var(--accent)", padding: "2px 6px" }} onClick={() => setShowLog(true)}>
            View change log
          </Button>
        </div>
        {!isAdmin && (
          <Badge tone="warning" style={{ fontSize: 12, padding: "4px 10px" }}>
            🔒 Admin only — read-only view
          </Badge>
        )}
        {isAdmin && (
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        )}
      </div>

      {/* 2026-10-27 (owner): weeks whose return window is still open are left out of profit totals ("Settled weeks only" in Insight). Saved with Save Settings; the change log records old -> new. */}
      <div data-testid="return-lag" style={{ ...cardBox, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <label htmlFor="return-lag-input" style={{ fontSize: 13.5, fontWeight: 700, color: "var(--text)" }}>Return-window lag (weeks)</label>
        <input id="return-lag-input" type="number" min={0} max={8} step={1} disabled={disabled} value={settings.return_window_lag_weeks ?? 3}
          onChange={(e) => setSettings((prev) => ({ ...prev, return_window_lag_weeks: e.target.value === "" ? "" : Math.max(0, Math.min(8, Math.round(Number(e.target.value)))) }))}
          style={{ width: 70, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--card)", color: "var(--text)" }} />
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Profit totals skip weeks that ended less than this many weeks ago, so returns have time to arrive. 0 to 8, default 3.</span>
      </div>

      <Tabs tabs={CHANNEL_TABS.map((t) => ({ id: t.key, label: t.label }))} value={activeCh} onChange={setActiveCh} />

      {SIMPLE_ONLY.includes(activeCh) && (
        <SimpleChannelPanel ch={activeCh} settings={settings} onUpdate={onUpdate} categories={categories} disabled={disabled} />
      )}
      {activeCh === "flipkart" && (
        <div data-testid="flipkart-mirror" style={{ ...cardBox, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, fontWeight: 700, color: "var(--text)", cursor: disabled ? "default" : "pointer" }}>
            <input type="checkbox" role="switch" aria-label="Use Myntra's rates for Flipkart" checked={flipkartMirror} disabled={disabled}
              onChange={(e) => setSettings((prev) => ({ ...prev, flipkart_mirror_myntra: e.target.checked }))} />
            Use Myntra's rates for Flipkart
          </label>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {flipkartMirror ? "On: every profit view prices Flipkart with Myntra's commission, GT, fixed fee and reverse logistics." : "Off: Flipkart uses the rates below; with none set, its charges count as ₹0."}
          </span>
          {flipkartMirror && <Badge tone="warning" style={{ fontSize: 11, padding: "2px 8px" }}>mirroring Myntra</Badge>}
        </div>
      )}
      {CH_COMPLEX.includes(activeCh) && (
        <div style={activeCh === "flipkart" && flipkartMirror ? { opacity: 0.55 } : undefined}>
          <ComplexChannelPanel ch={activeCh} settings={settings} onUpdate={onUpdate} onGTUpdate={onRootUpdate} categories={categories} disabled={disabled || (activeCh === "flipkart" && flipkartMirror)} />
        </div>
      )}

      <ProductCategoriesPanel products={categories} onChange={onProductsChange} disabled={disabled} />

      {isAdmin && <InsightCacheCard showToast={showToast} />}

      {showLog && <ChangeLogModal onClose={() => setShowLog(false)} showToast={showToast} />}
    </div>
  );
}

const th = { textAlign: "left" };
const td = {};
